/**
 * Main Application Component
 *
 * Sets up routing and the main application structure.
 * Routes are protected by authentication where required.
 *
 * Route structure (channel-centric):
 *   /                                  Dashboard
 *   /channels                          Channels list (+ add channel)
 *   /channels/:id                      Channel overview (ChannelDetail)
 *   /channels/:channelId/chatters      Chatter index (?chatter=<id> opens detail)
 *   /channels/:channelId/commands      Per-channel chat commands
 *   /channels/:channelId/modules       Per-channel module config
 *   /channels/:channelId/modules/:moduleName   A module's channel settings
 *   /channels/:channelId/overlays      Channel overlays
 *   /channels/:channelId/overlays/...  Overlay editors
 *   /groups                            Channel groups (stub)
 *   /admin/modules                     Module registry (admin)
 *   /admin/commands                    Chat command catalog (admin)
 *   /admin/bots                        Bot accounts (admin)
 *   /admin/break-glass                 Emergency sign-in (PUBLIC, outside Layout)
 *   /settings                          Account settings (sidebar footer)
 *   /settings/bot                      Bot override (connect your own bot)
 */

import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Login from './pages/Login'
import AuthCallback from './pages/AuthCallback'
import Channels from './pages/Channels'
import ChannelDetail from './pages/ChannelDetail'
import ChannelChatters from './pages/ChannelChatters'
import ChannelCommands from './pages/ChannelCommands'
import Overlays from './pages/Overlays'
import OverlayEditor from './pages/OverlayEditor'
import CustomOverlayEditor from './pages/CustomOverlayEditor'
import Modules from './pages/Modules'
import ModuleSettings from './pages/ModuleSettings'
import Settings from './pages/Settings'
import BotOverride from './pages/BotOverride'
import AdminBots from './pages/AdminBots'
import AdminModules from './pages/AdminModules'
import AdminCommands from './pages/AdminCommands'
import BreakGlass from './pages/BreakGlass'

/**
 * ProtectedRoute - redirects to login if not authenticated.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-app">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-synth-magenta"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

/**
 * AdminRoute - requires an authenticated admin user.
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  if (!user?.isAdmin) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

/**
 * App - main application with routing configuration.
 */
export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/complete" element={<AuthCallback />} />
      <Route path="/auth/error" element={<Login />} />

      {/* Break-glass. Sits HERE, not under the protected tree, despite the
          /admin/ prefix: it is the way in for someone who has no session, so
          wrapping it in ProtectedRoute would bounce the one caller it exists
          for. No collision with the nested admin routes below, which only
          define `admin/modules` and `admin/bots`. Deliberately unlinked from
          the login page — the URL is documented in
          `architecture/multi-user-architecture.md`. */}
      <Route path="/admin/break-glass" element={<BreakGlass />} />

      {/* Protected routes with layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />

        {/* Channels */}
        <Route path="channels" element={<Channels />} />
        <Route path="channels/:id" element={<ChannelDetail />} />
        {/* Chatter index (R18f). The per-chatter detail is the SAME modal
            the chat log opens, driven by `?chatter=<twitch_user_id>` —
            deep-linkable, and the back button closes it. No second route:
            see the header of ChannelChatters.tsx for why the chat-log
            entry point deliberately stayed a modal. */}
        <Route path="channels/:channelId/chatters" element={<ChannelChatters />} />

        {/* Per-channel commands. The GLOBAL catalog and the policy over it
            live at /admin/commands; this is what one channel does with it. */}
        <Route
          path="channels/:channelId/commands"
          element={<ChannelCommands />}
        />

        {/* Per-channel modules */}
        <Route path="channels/:channelId/modules" element={<Modules />} />
        <Route
          path="channels/:channelId/modules/:moduleName"
          element={<ModuleSettings />}
        />

        {/* Per-channel overlays */}
        <Route path="channels/:channelId/overlays" element={<Overlays />} />
        <Route
          path="channels/:channelId/overlays/:overlayId"
          element={<OverlayEditor />}
        />
        <Route
          path="channels/:channelId/overlays/custom/:overlayId"
          element={<CustomOverlayEditor />}
        />

        {/* Groups (stub — reuses Channels view until built) */}
        <Route path="groups" element={<Channels />} />

        {/* Admin */}
        <Route
          path="admin/modules"
          element={
            <AdminRoute>
              <AdminModules />
            </AdminRoute>
          }
        />
        <Route
          path="admin/commands"
          element={
            <AdminRoute>
              <AdminCommands />
            </AdminRoute>
          }
        />
        <Route
          path="admin/bots"
          element={
            <AdminRoute>
              <AdminBots />
            </AdminRoute>
          }
        />

        {/* Account settings (account-scoped, reached from the sidebar footer) */}
        <Route path="settings" element={<Settings />} />
        {/* Bot override. Account-scoped like Settings, NOT per-channel: one bot
            substitutes for the system bot across every channel the user owns. */}
        <Route path="settings/bot" element={<BotOverride />} />

        {/* Back-compat: old flat module route redirects to admin registry */}
        <Route path="modules" element={<Navigate to="/admin/modules" replace />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
